import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, Plus, Search, Copy, Trash2, Edit, Eye, Send,
  FileText, Briefcase, GraduationCap, Home, Shield, Heart,
  ChevronRight, X, Type, Image, MousePointer, Minus, Columns,
  Monitor, Smartphone, Save, Loader2, Variable, Check
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useBusinessContent } from '../contexts/BusinessContentContext';
import EmailTemplatePreview from '../components/EmailTemplatePreview';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Inline preview modal wrapper
const EmailTemplatePreviewModal = ({ isOpen, onClose, businessType }) => {
  return <EmailTemplatePreview isOpen={isOpen} onClose={onClose} />;
};

// Category icons
const CATEGORY_ICONS = {
  onboarding: Mail,
  nurturing: Heart,
  scheduling: FileText,
  transactional: Briefcase,
  education: GraduationCap,
  insurance: Shield,
  'real-estate': Home
};

const CATEGORIES = [
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'nurturing', label: 'Nurturing' },
  { value: 'scheduling', label: 'Scheduling' },
  { value: 'transactional', label: 'Transactional' },
  { value: 'education', label: 'Education' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'real-estate', label: 'Real Estate' }
];

const EmailTemplates = () => {
  // Get business content for filtering templates by business type
  const { getEmailTemplates, getBusinessTypeName, businessType } = useBusinessContent();
  
  const [systemTemplates, setSystemTemplates] = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [variables, setVariables] = useState([]);
  const [testData, setTestData] = useState({});
  const [sendingTest, setSendingTest] = useState(false);
  const [showRichPreview, setShowRichPreview] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const [allRes, varsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/email-templates/all`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/email-templates/variables/list`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setSystemTemplates(allRes.data.system_templates || []);
      setCustomTemplates(allRes.data.custom_templates || []);
      setVariables(varsRes.data.variables || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleDuplicate = async (template) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(`${BACKEND_URL}/api/email-templates/${template.id}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template duplicated');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to duplicate template');
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm('Delete this template?')) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${BACKEND_URL}/api/email-templates/${template.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handlePreview = async (template) => {
    setSelectedTemplate(template);
    setShowPreview(true);
    
    // Initialize test data with defaults
    const defaultData = {};
    variables.forEach(v => {
      defaultData[v.key] = v.example;
    });
    setTestData(defaultData);
    
    await generatePreview(template.id, defaultData);
  };

  const generatePreview = async (templateId, data) => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.post(
        `${BACKEND_URL}/api/email-templates/preview?template_id=${templateId}`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreviewHtml(res.data.html);
    } catch (error) {
      console.error('Failed to generate preview:', error);
    }
  };

  const handleTestDataChange = (key, value) => {
    const newData = { ...testData, [key]: value };
    setTestData(newData);
    if (selectedTemplate) {
      generatePreview(selectedTemplate.id, newData);
    }
  };

  const handleSendTest = async () => {
    const testEmail = testData.email || '';
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }
    
    setSendingTest(true);
    
    // Simulate sending (in production, this would call a real email API)
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    toast.success(`Test email sent to ${testEmail}`);
    setSendingTest(false);
  };

  const filteredSystemTemplates = systemTemplates.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || t.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const filteredCustomTemplates = customTemplates.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || t.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-emerald-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div data-testid="email-templates-page" className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-4 md:p-6">
      {/* Rich Preview Modal */}
      {showRichPreview && (
        <EmailTemplatePreviewModal 
          isOpen={showRichPreview}
          onClose={() => setShowRichPreview(false)}
          businessType={businessType}
        />
      )}
      
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 md:mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Mail className="w-7 h-7 md:w-8 md:h-8 text-emerald-400" />
            {getBusinessTypeName()} Email Templates
          </h1>
          <p className="text-emerald-300/70 text-sm md:text-base">Create and manage email templates with drag-and-drop editor</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowRichPreview(true)}
            variant="outline"
            className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
          >
            <Eye className="w-4 h-4 mr-2" />
            Preview Rich Templates
          </Button>
          <Button
            onClick={() => setShowEditor(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white w-full md:w-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Template
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-[#052e22]/50 border-emerald-800/30 mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-emerald-500/50" />
              <Input
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#0a3d2e] border-emerald-800/30 text-white placeholder:text-emerald-300/40"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full md:w-48 bg-[#0a3d2e] border-emerald-800/30 text-white">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* System Templates */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-emerald-400" />
          Pre-built Templates
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSystemTemplates.map((template, index) => {
            const CategoryIcon = CATEGORY_ICONS[template.category] || Mail;
            return (
              <motion.div
                key={template.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="bg-[#052e22]/50 border-emerald-800/30 hover:border-emerald-600/50 transition-all cursor-pointer group"
                      onClick={() => handlePreview(template)}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="p-2.5 rounded-lg bg-emerald-500/20">
                        <CategoryIcon className="w-5 h-5 text-emerald-400" />
                      </div>
                      <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs">
                        System
                      </Badge>
                    </div>
                    <h3 className="text-white font-semibold mb-1">{template.name}</h3>
                    <p className="text-sm text-emerald-300/70 mb-3 line-clamp-2">{template.description}</p>
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="text-xs border-emerald-800/50 text-emerald-400">
                        {template.category}
                      </Badge>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-emerald-400"
                                onClick={(e) => { e.stopPropagation(); handlePreview(template); }}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-blue-400"
                                onClick={(e) => { e.stopPropagation(); handleDuplicate(template); }}>
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Custom Templates */}
      <div>
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <Edit className="w-5 h-5 text-emerald-400" />
          My Templates
        </h2>
        {filteredCustomTemplates.length === 0 ? (
          <Card className="bg-[#052e22]/30 border-emerald-800/30 border-dashed">
            <CardContent className="py-12 text-center">
              <Mail className="w-16 h-16 text-emerald-800 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No custom templates</h3>
              <p className="text-emerald-300/70 mb-4">Create your own or duplicate a system template</p>
              <Button onClick={() => setShowEditor(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Plus className="w-4 h-4 mr-2" />
                Create Template
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCustomTemplates.map((template, index) => {
              const CategoryIcon = CATEGORY_ICONS[template.category] || Mail;
              return (
                <motion.div
                  key={template.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="bg-[#052e22]/50 border-emerald-800/30 hover:border-emerald-600/50 transition-all cursor-pointer group"
                        onClick={() => handlePreview(template)}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="p-2.5 rounded-lg bg-blue-500/20">
                          <CategoryIcon className="w-5 h-5 text-blue-400" />
                        </div>
                        <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs">
                          Custom
                        </Badge>
                      </div>
                      <h3 className="text-white font-semibold mb-1">{template.name}</h3>
                      <p className="text-sm text-emerald-300/70 mb-3 line-clamp-2">{template.description}</p>
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className="text-xs border-emerald-800/50 text-emerald-400">
                          {template.category}
                        </Badge>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-emerald-400"
                                  onClick={(e) => { e.stopPropagation(); handlePreview(template); }}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-blue-400"
                                  onClick={(e) => { e.stopPropagation(); handleDuplicate(template); }}>
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-400"
                                  onClick={(e) => { e.stopPropagation(); handleDelete(template); }}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      <AnimatePresence>
        {showPreview && selectedTemplate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowPreview(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#0a1f18] border border-emerald-800/30 rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col md:flex-row"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Variables Panel */}
              <div className="w-full md:w-80 border-b md:border-b-0 md:border-r border-emerald-800/30 p-4 md:p-6 overflow-y-auto max-h-[30vh] md:max-h-none">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Variable className="w-5 h-5 text-emerald-400" />
                    Test Data
                  </h3>
                  <button onClick={() => setShowPreview(false)} className="md:hidden p-2 hover:bg-emerald-900/50 rounded-lg">
                    <X className="w-5 h-5 text-emerald-400" />
                  </button>
                </div>
                <p className="text-sm text-emerald-300/70 mb-4">Edit values to see live preview</p>
                
                <div className="space-y-3">
                  {variables.slice(0, 6).map((v) => (
                    <div key={v.key}>
                      <label className="text-xs text-emerald-300/70 block mb-1">
                        {`{{${v.key}}}`}
                      </label>
                      <Input
                        value={testData[v.key] || ''}
                        onChange={(e) => handleTestDataChange(v.key, e.target.value)}
                        className="bg-[#052e22] border-emerald-800/30 text-white text-sm h-9"
                        placeholder={v.example}
                      />
                    </div>
                  ))}
                </div>

                <div className="mt-6 space-y-2">
                  <Button
                    onClick={handleSendTest}
                    disabled={sendingTest}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {sendingTest ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" />Send Test Email</>
                    )}
                  </Button>
                  <Button
                    onClick={() => handleDuplicate(selectedTemplate)}
                    variant="outline"
                    className="w-full border-emerald-800/50 text-emerald-400 hover:bg-emerald-900/30"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Duplicate & Edit
                  </Button>
                </div>
              </div>

              {/* Preview Panel */}
              <div className="flex-1 flex flex-col overflow-hidden">
                <div className="p-4 border-b border-emerald-800/30 flex items-center justify-between">
                  <div>
                    <h2 className="text-white font-semibold">{selectedTemplate.name}</h2>
                    <p className="text-sm text-emerald-300/70">Subject: {selectedTemplate.subject}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPreviewDevice('desktop')}
                      className={`p-2 rounded-lg transition-colors ${previewDevice === 'desktop' ? 'bg-emerald-600 text-white' : 'text-emerald-400 hover:bg-emerald-900/30'}`}
                    >
                      <Monitor className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setPreviewDevice('mobile')}
                      className={`p-2 rounded-lg transition-colors ${previewDevice === 'mobile' ? 'bg-emerald-600 text-white' : 'text-emerald-400 hover:bg-emerald-900/30'}`}
                    >
                      <Smartphone className="w-5 h-5" />
                    </button>
                    <button onClick={() => setShowPreview(false)} className="hidden md:block p-2 hover:bg-emerald-900/50 rounded-lg">
                      <X className="w-5 h-5 text-emerald-400" />
                    </button>
                  </div>
                </div>
                
                <div className="flex-1 overflow-auto p-4 md:p-6 bg-slate-800/50 flex justify-center">
                  <div 
                    className={`bg-white rounded-lg shadow-xl overflow-hidden transition-all ${
                      previewDevice === 'mobile' ? 'w-[375px]' : 'w-full max-w-[600px]'
                    }`}
                    style={{ minHeight: '400px' }}
                  >
                    <iframe
                      srcDoc={previewHtml}
                      className="w-full h-full min-h-[400px]"
                      title="Email Preview"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Simple Template Creator */}
      <AnimatePresence>
        {showEditor && (
          <TemplateEditor 
            onClose={() => setShowEditor(false)}
            onSave={() => {
              setShowEditor(false);
              fetchTemplates();
            }}
            categories={CATEGORIES}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Simple Template Editor Component
const TemplateEditor = ({ onClose, onSave, categories }) => {
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('nurturing');
  const [description, setDescription] = useState('');
  const [sections, setSections] = useState([
    { id: '1', type: 'header', content: { text: 'Hello {{first_name}}!', level: 1, align: 'center' }, order: 0 },
    { id: '2', type: 'text', content: { text: 'Your email content goes here...', align: 'left' }, order: 1 },
    { id: '3', type: 'button', content: { text: 'Click Here', url: '{{meeting_link}}', color: '#0a5c44' }, order: 2 }
  ]);
  const [saving, setSaving] = useState(false);

  const addSection = (type) => {
    const newSection = {
      id: Date.now().toString(),
      type,
      content: type === 'header' 
        ? { text: 'New Heading', level: 2, align: 'center' }
        : type === 'text'
        ? { text: 'New paragraph text...', align: 'left' }
        : type === 'button'
        ? { text: 'Button', url: '#', color: '#0a5c44' }
        : type === 'divider'
        ? {}
        : { height: '20px' },
      order: sections.length
    };
    setSections([...sections, newSection]);
  };

  const updateSection = (id, content) => {
    setSections(sections.map(s => s.id === id ? { ...s, content: { ...s.content, ...content } } : s));
  };

  const removeSection = (id) => {
    setSections(sections.filter(s => s.id !== id));
  };

  const handleSave = async () => {
    if (!name || !subject) {
      toast.error('Please fill in name and subject');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(`${BACKEND_URL}/api/email-templates`, {
        name,
        subject,
        category,
        description,
        sections,
        styles: { backgroundColor: '#ffffff', primaryColor: '#0a5c44', fontFamily: 'Arial, sans-serif' }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template created!');
      onSave();
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        className="bg-[#0a1f18] border border-emerald-800/30 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="p-4 md:p-6 border-b border-emerald-800/30 flex justify-between items-center">
          <h2 className="text-xl font-bold text-white">Create Email Template</h2>
          <button onClick={onClose} className="p-2 hover:bg-emerald-900/50 rounded-lg">
            <X className="w-5 h-5 text-emerald-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="text-sm text-emerald-300/70 block mb-1">Template Name</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My Welcome Email"
                className="bg-[#052e22] border-emerald-800/30 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-emerald-300/70 block mb-1">Category</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="bg-[#052e22] border-emerald-800/30 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mb-6">
            <label className="text-sm text-emerald-300/70 block mb-1">Email Subject</label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Welcome to {{company_name}}, {{first_name}}!"
              className="bg-[#052e22] border-emerald-800/30 text-white"
            />
          </div>

          <div className="mb-6">
            <label className="text-sm text-emerald-300/70 block mb-1">Description (optional)</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of when to use this template"
              className="bg-[#052e22] border-emerald-800/30 text-white resize-none"
              rows={2}
            />
          </div>

          {/* Section Builder */}
          <div className="mb-4">
            <label className="text-sm text-emerald-300/70 block mb-2">Email Content</label>
            <div className="flex gap-2 flex-wrap mb-4">
              <Button size="sm" variant="outline" onClick={() => addSection('header')}
                      className="border-emerald-800/50 text-emerald-400 hover:bg-emerald-900/30">
                <Type className="w-4 h-4 mr-1" /> Header
              </Button>
              <Button size="sm" variant="outline" onClick={() => addSection('text')}
                      className="border-emerald-800/50 text-emerald-400 hover:bg-emerald-900/30">
                <FileText className="w-4 h-4 mr-1" /> Text
              </Button>
              <Button size="sm" variant="outline" onClick={() => addSection('button')}
                      className="border-emerald-800/50 text-emerald-400 hover:bg-emerald-900/30">
                <MousePointer className="w-4 h-4 mr-1" /> Button
              </Button>
              <Button size="sm" variant="outline" onClick={() => addSection('divider')}
                      className="border-emerald-800/50 text-emerald-400 hover:bg-emerald-900/30">
                <Minus className="w-4 h-4 mr-1" /> Divider
              </Button>
            </div>

            <div className="space-y-3">
              {sections.map((section) => (
                <div key={section.id} className="p-4 bg-[#052e22] border border-emerald-800/30 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs capitalize">
                      {section.type}
                    </Badge>
                    <Button size="sm" variant="ghost" onClick={() => removeSection(section.id)}
                            className="h-7 w-7 p-0 text-red-400 hover:bg-red-900/30">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  
                  {section.type === 'header' && (
                    <Input
                      value={section.content.text}
                      onChange={(e) => updateSection(section.id, { text: e.target.value })}
                      className="bg-[#0a3d2e] border-emerald-800/30 text-white"
                      placeholder="Header text..."
                    />
                  )}
                  
                  {section.type === 'text' && (
                    <Textarea
                      value={section.content.text}
                      onChange={(e) => updateSection(section.id, { text: e.target.value })}
                      className="bg-[#0a3d2e] border-emerald-800/30 text-white resize-none"
                      rows={3}
                      placeholder="Paragraph text..."
                    />
                  )}
                  
                  {section.type === 'button' && (
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        value={section.content.text}
                        onChange={(e) => updateSection(section.id, { text: e.target.value })}
                        className="bg-[#0a3d2e] border-emerald-800/30 text-white"
                        placeholder="Button text"
                      />
                      <Input
                        value={section.content.url}
                        onChange={(e) => updateSection(section.id, { url: e.target.value })}
                        className="bg-[#0a3d2e] border-emerald-800/30 text-white"
                        placeholder="URL"
                      />
                    </div>
                  )}
                  
                  {section.type === 'divider' && (
                    <div className="h-px bg-emerald-800/30" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="p-4 md:p-6 border-t border-emerald-800/30 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}
                  className="border-emerald-800/50 text-emerald-400 hover:bg-emerald-900/30">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Template</>}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default EmailTemplates;
